import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { Spinner } from '../../../components/Spinner'
import { OfHtml } from '../../OfHtml'
import { useCursorList, type CursorPage } from '../../useCursorList'
import { Button, Modal } from '../../settings/kit'
import {
  OF_TRANSACTIONS,
  periodDates,
  periodFromQuery,
  periodItems,
  periodRange,
  periodTitle,
  toTxPage,
  TX_TYPES,
  txAmount,
  txDate,
  txQuery,
  userStart,
  utcOffset,
  isoLocal,
  type PageQuery,
  type Period,
  type PeriodType,
  type Transaction,
  type TxType,
} from './cardsData'
import { useCards } from './context'
import { BrandIcon, EmptyPayments, OfOnly } from './parts'

type Label = { key: string; values?: Record<string, unknown> } | { text: string }

function useLabel() {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  return (l: Label) => ('text' in l ? l.text : t(l.key, l.values))
}

/** TransactionsList: Date | Amount, one row per payment. */
export function TransactionsTable({ transactions }: { transactions: Transaction[] }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  return (
    <table className="ofc-table">
      <thead>
        <tr>
          <th>{t('tx.date')}</th>
          <th className="ofc-right" colSpan={2}>
            {t('tx.amount')}
          </th>
        </tr>
      </thead>
      <tbody>
        {transactions.map((tx) => (
          <TransactionRow key={tx.id} tx={tx} />
        ))}
      </tbody>
    </table>
  )
}

function TransactionRow({ tx }: { tx: Transaction }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { lang } = useCards()
  const failed = tx.status === 'failure'
  const { amount, taxes } = txAmount(tx)
  const hasCard = tx.source === 'card' || tx.source === 'alternative'
  const status =
    tx.status === 'done'
      ? { icon: 'check', cls: 'is-ok', tip: t('tx.paid') }
      : tx.status === 'undo'
        ? { icon: 'rotate-ccw', cls: 'is-muted', tip: t('tx.refunded') }
        : failed
          ? { icon: 'alert-circle', cls: 'is-danger', tip: t('tx.failed') }
          : null
  return (
    <tr className={failed ? 'is-failed' : undefined}>
      <td className="ofc-tx-when">
        <span className="ofc-tx-date">{txDate(tx.createdAt, lang)}</span>
        <div className="ofc-tx-holder">
          {hasCard ? (
            <BrandIcon brand={tx.brand} />
          ) : (
            <span className="ofc-tx-source" aria-hidden="true">
              <FeatherIcon name={tx.source === 'wallet' ? 'briefcase' : 'credit-card'} size={16} />
            </span>
          )}
          <span>{hasCard ? tx.identifier : t('tx.walletCredit')}</span>
        </div>
      </td>
      <td className={`ofc-tx-amount ${failed ? 'is-danger' : ''}`}>
        <strong>
          {amount}
          {taxes && ` (${taxes})`}
        </strong>
      </td>
      <td className="ofc-tx-desc">
        <div className="ofc-tx-state">
          <div className={tx.userInactive ? 'is-inactive' : undefined}>
            <OfHtml html={tx.description} className="ofc-tx-text" />
            {failed && tx.retryType && (
              <div className="ofc-tx-retry">
                <button type="button" className="ofc-pill-btn" disabled>
                  <FeatherIcon name="refresh-cw" size={12} />
                  {t('tx.retry')}
                </button>
                <OfOnly text={t('only.retry')} href={OF_TRANSACTIONS} />
              </div>
            )}
          </div>
          {status && (
            <span className={`ofc-tx-status ${status.cls}`} title={status.tip} aria-label={status.tip}>
              <FeatherIcon name={status.icon} size={18} />
            </span>
          )}
        </div>
      </td>
    </tr>
  )
}

/** /my/payments/list: period, filter pills, every transaction (marker paging). */
export function TransactionsTab({ query, onQuery }: { query: PageQuery; onQuery: (next: Partial<PageQuery>) => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { of, referer, me, hasTransactions, hasTransactionsLoading } = useCards()
  const type: TxType = query.type ?? 'all'
  const fetchPage = useCallback(
    async (cursor: string | null): Promise<CursorPage<Transaction>> => {
      const page = toTxPage(
        await of.get('payments/all/transactions', txQuery({ marker: cursor, type, dateFrom: query.dateFrom, dateTo: query.dateTo }), referer),
      )
      return { list: page.list, hasMore: page.hasMore, next: page.nextMarker === null ? null : String(page.nextMarker) }
    },
    // A new filter or period starts the list over (refreshTransactions).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [of, type, query.dateFrom, query.dateTo],
  )
  const empty = type === 'error' ? t('tx.noErrors') : t('tx.none')

  if (hasTransactionsLoading) return <Loader />
  if (!hasTransactions) return <EmptyPayments text={t('tx.none')} />
  return (
    <div className="ofc-transactions">
      <PeriodPicker query={query} startDate={me.startDate} onQuery={onQuery} />
      <div className="ofc-pills" role="tablist" aria-label={t('tabs.payments')}>
        {TX_TYPES.map((id) => (
          <button key={id} type="button" role="tab" aria-selected={id === type} className={`ofc-pill ${id === type ? 'is-on' : ''}`} onClick={() => onQuery({ type: id })}>
            {t(`filters.${id}`)}
          </button>
        ))}
      </div>
      <TxList fetchPage={fetchPage} empty={empty} />
    </div>
  )
}

const txId = (tx: Transaction) => tx.id

function TxList({ fetchPage, empty }: { fetchPage: (cursor: string | null) => Promise<CursorPage<Transaction>>; empty: string }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { items: loaded, hasMore, loading, failed, loadMore } = useCursorList(fetchPage, txId)
  // A page answered twice (a remount while it loaded) must not show its rows twice.
  const items = useMemo(() => {
    const seen = new Set<string>()
    return loaded.filter((tx) => !seen.has(tx.id) && !!seen.add(tx.id))
  }, [loaded])
  return (
    <>
      {items.length > 0 && <TransactionsTable transactions={items} />}
      {loading && <Loader />}
      {failed && (
        <div className="ofc-empty is-small">
          <span>{t('tx.error')}</span>
          <Button variant="primary" onClick={() => void loadMore()}>
            {t('tx.retry')}
          </Button>
        </div>
      )}
      {!loading && !failed && !items.length && !hasMore && <EmptyPayments text={empty} />}
      {hasMore && !loading && !failed && items.length > 0 && <Sentinel onSeen={() => void loadMore()} />}
    </>
  )
}

/** Loads the next page when it scrolls into view (vue-infinite-loading). */
function Sentinel({ onSeen }: { onSeen: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const cb = useRef(onSeen)
  cb.current = onSeen
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && cb.current(), { rootMargin: '200px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return <div ref={ref} className="ofc-sentinel" />
}

export function Loader() {
  return (
    <div className="ofc-loader">
      <Spinner size={26} />
    </div>
  )
}

/** PeriodDatepicker: the period's title and dates, and OnlyFans' list of periods. */
function PeriodPicker({ query, startDate, onQuery }: { query: PageQuery; startDate: string; onQuery: (next: Partial<PageQuery>) => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { intl } = useCards()
  const label = useLabel()
  const [open, setOpen] = useState(false)
  const [custom, setCustom] = useState<{ start: string; end: string } | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const now = useMemo(() => new Date(), [])
  const start = useMemo(() => userStart({ startDate }, now), [startDate, now])
  const period = periodFromQuery(query, start, now)
  const items = periodItems(start, now, intl)
  const { from, to } = periodDates(period, intl)

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false)
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const apply = (p: Period) =>
    onQuery(p.type === 'all' ? { dateType: null, dateFrom: '', dateTo: '' } : { dateType: p.type, dateFrom: isoLocal(p.start), dateTo: isoLocal(p.end) })
  const pick = (type: PeriodType, value: number | null) => {
    setOpen(false)
    apply(periodRange(type, value, start, new Date()))
  }
  const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  return (
    <div className="ofc-period" ref={ref}>
      <button type="button" className="ofc-period-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>
          <strong>{label(periodTitle(period, intl))}</strong>
          <small>
            {from} - {to}
            {` (${t('period.localTime', { utc: utcOffset(now) })})`}
          </small>
        </span>
        <FeatherIcon name="chevron-down" size={20} />
      </button>
      {open && (
        <div className="ofc-menu ofc-period-menu" role="menu">
          {items.map((it) => (
            <div key={it.key} role="none">
              {it.divider && <div className="ofc-menu-divider" />}
              <button
                type="button"
                role="menuitem"
                className="ofc-menu-item"
                onClick={() => {
                  if (it.pick === 'custom') {
                    setOpen(false)
                    setCustom({ start: day(period.start), end: day(period.end) })
                  } else pick(it.pick.type, it.pick.value)
                }}
              >
                {label(it.label)}
              </button>
            </div>
          ))}
        </div>
      )}
      {custom && (
        <Modal
          title={t('period.custom')}
          onClose={() => setCustom(null)}
          actions={
            <>
              <Button variant="flat" onClick={() => setCustom(null)}>
                {t('period.cancel')}
              </Button>
              <Button
                variant="flat"
                disabled={!custom.start || !custom.end || custom.start > custom.end}
                onClick={() => {
                  const r = custom
                  setCustom(null)
                  apply(periodRange('custom', r, start, new Date()))
                }}
              >
                {t('period.apply')}
              </Button>
            </>
          }
        >
          <div className="ofc-custom">
            <label>
              <span>{t('period.from')}</span>
              <input type="date" className="ofc-date" value={custom.start} min={day(start)} max={custom.end || day(now)} onChange={(e) => setCustom({ ...custom, start: e.target.value })} />
            </label>
            <label>
              <span>{t('period.to')}</span>
              <input type="date" className="ofc-date" value={custom.end} min={custom.start} max={day(now)} onChange={(e) => setCustom({ ...custom, end: e.target.value })} />
            </label>
          </div>
        </Modal>
      )}
    </div>
  )
}
